import { differenceInYears, parse } from 'date-fns';
import delv from 'dlv';
import { isEqual, isNil } from 'lodash';

import { CommunicationPreferences } from 'state/auth';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { getName } from 'utils/attributes';
import * as Braze from 'utils/braze';
import { isNativeIOS } from 'utils/environment';
import { getNativeLocationPermissions } from 'utils/geolocation';

import { CustomEventNames, EventTypes } from './constants';
import { IMParticleAttributes, IMParticleCtx, IMParticleUser } from './types';
import { normalizeBooleans, sanitizeValues } from './utils';

const getAge = ({
  age,
  dob,
  updatedAttributes,
}: {
  age?: number | null;
  dob?: string | null;
  updatedAttributes?: UpdatedUserAttributes;
}): number | undefined | null => {
  if (updatedAttributes?.dobDeleted === 'True') {
    return null;
  }
  if (dob) {
    return differenceInYears(new Date(), parse(dob, 'yyyy-MM-dd', new Date()));
  }
  return age ?? null;
};

const flattenCommunicationPreferences = (userCommPreferences?: CommunicationPreferences | null) =>
  isNil(userCommPreferences)
    ? {}
    : userCommPreferences?.reduce?.(
        (acc, commPreference) =>
          commPreference.id !== 'email_subscribe' && commPreference.id !== 'marketingEmail'
            ? { ...acc, [commPreference.id]: commPreference.value.toLowerCase() }
            : acc,
        {}
      );

type UpdatedUserAttributes = Partial<UserDetails['details']> & {
  'Legacy User'?: string;
};

// safely merge existing and new attributes
export const mergeUserAttributes = (
  {
    $FirstName,
    $LastName,
    $Zip: zipcode,
    $Age: age,
    $Gender: gender,
    $City: city,
    $State: state,
    $Mobile: phoneNumber,
    'Email Opt In': promotionalEmails,
    'Location Services': locationServices,
    Locale: locale,
    Tier,
    Timezone: timezone,
    'Join Date': joinDate,
    'Legacy User': legacyUser,
    'Date of Birth': dob,
    'RBI Cognito ID': rbiCognitoId,
    favoriteStores,
    favoriteOffers,
    language,
    paybackUserId,
    ...rest
  }: IMParticleAttributes = {},
  updatedAttributes: UpdatedUserAttributes = {},
  enablePushNotificiationsOnSignUp?: boolean
): Partial<IMParticleAttributes> => {
  const getAttr = (name: string, fallback = ''): string =>
    delv(updatedAttributes, name) || fallback;

  const flattenedCommunicationPreferences = flattenCommunicationPreferences(
    updatedAttributes.communicationPreferences
  );

  const isBrazeForwarderConfigured = window.mParticle
    ?._getActiveForwarders?.()
    ?.find(f => f.name.toLowerCase() === 'appboy' || f.name.toLowerCase() === 'braze');

  const getDob = () => {
    if (isBrazeForwarderConfigured && updatedAttributes?.dobDeleted === 'True') {
      //Workaround for removing the DOB directly on Braze (context: ICU-44)
      //see braze-compat.ts l54 & l55 - verify if initialization was successful
      if (Braze?.initialize()) {
        Braze?.getUser?.()?.setDateOfBirth(null, null, null);
      }
      return null;
    }
    return dob !== updatedAttributes?.dob ? updatedAttributes?.dob : dob;
  };

  const getPhoneNumber = () => {
    if (isBrazeForwarderConfigured && !updatedAttributes?.phoneNumber) {
      //Workaround for removing the Phone Number directly on Braze (context: ICU-44)
      //see braze-compat.ts l54 & l55 - verify if initialization was successful
      if (Braze?.initialize()) {
        Braze?.getUser?.()?.setPhoneNumber(null);
      }
      return null;
    }
    return phoneNumber !== updatedAttributes?.phoneNumber
      ? updatedAttributes?.phoneNumber
      : phoneNumber;
  };

  const getTier = () => {
    const updatedTier = (updatedAttributes?.loyaltyUserTier ?? '').replace(/[^0-9]/g, '') || null;
    return Tier !== updatedTier ? updatedTier : Tier;
  };

  const getPushOptions = () => {
    if (enablePushNotificiationsOnSignUp) {
      return {
        push_subscribe:
          getAttr('promotionalEmails', promotionalEmails).toLowerCase() === 'true'
            ? 'opted_in'
            : 'unsubscribed',
        marketingPush: getAttr('promotionalEmails', promotionalEmails),
        rewardsPush: getAttr('promotionalEmails', promotionalEmails),
      };
    }

    return {};
  };

  const pushOptions = getPushOptions();

  const sanitizedValues = sanitizeValues({
    ...rest,
    ...getName(updatedAttributes, { $FirstName, $LastName }),
    $Zip: getAttr('zipcode', zipcode),
    $Gender: getAttr('gender', gender),
    $City: getAttr('city', city),
    $State: getAttr('state', state),
    'Email Opt In': getAttr('promotionalEmails', promotionalEmails),
    email_subscribe:
      getAttr('promotionalEmails', promotionalEmails).toLowerCase() === 'true'
        ? 'opted_in'
        : 'unsubscribed',
    marketingEmail: getAttr('promotionalEmails', promotionalEmails),
    'Location Services': getAttr('locationServices', locationServices),
    Locale: getAttr('locale', locale),
    Timezone: getAttr('timezone', timezone),
    'Join Date': getAttr('joinDate', joinDate),
    'Legacy User': getAttr('Legacy User', legacyUser || ''),
    'RBI Cognito ID': getAttr('rbiCognitoId', rbiCognitoId),
    favoriteStores: getAttr('favoriteStores', favoriteStores),
    favoriteOffers: getAttr('favoriteOffers', favoriteOffers),
    language: getAttr('language', language),
    paybackUserId: getAttr('paybackUserId', paybackUserId),
    ...pushOptions,
    ...flattenedCommunicationPreferences,
  });

  return {
    ...sanitizedValues,
    //If the user deletes his birthday or Phone Number from the app we need to send null as a value
    'Date of Birth': getDob(), // Custom attribute
    dob: getDob(), // Braze's standard attribute
    $Age: getAge({ age, dob: getDob(), updatedAttributes }), // mParticle mapped attribute for dob
    $Mobile: getPhoneNumber(),
    Tier: getTier(),
  };
};

const setUserAttributes = (
  user: IMParticleUser,
  newAttributes: UpdatedUserAttributes,
  trackEvent: IMParticleCtx['trackEvent'],
  enablePushNotificiationsOnSignUp?: boolean
) => {
  const existingAttributes = user.getAllUserAttributes();
  const updated = mergeUserAttributes(
    existingAttributes,
    newAttributes,
    enablePushNotificiationsOnSignUp
  );

  if (!isEqual(existingAttributes, updated)) {
    trackEvent({
      name: CustomEventNames.UPDATE_USER_ATTRIBUTES,
      type: EventTypes.Other,
      attributes: {
        'Is Guest User': newAttributes.isGuest,
        'Promotional Emails': newAttributes.promotionalEmails,
        ...updated,
      },
    });
  }

  const normalizedUpdated = normalizeBooleans(updated);
  user.setUserAttributes(normalizedUpdated);
};

export const getUtmAttributes = (params: URLSearchParams) => {
  return sanitizeValues({
    'UTM Source': params.get('utm_source') || '',
    'UTM Medium': params.get('utm_medium') || '',
    'UTM Campaign': params.get('utm_campaign') || '',
    'UTM Term': params.get('utm_term') || '',
    'UTM Content': params.get('utm_content') || '',
  });
};

export const setUserUtmAttributes = (user: IMParticleUser | undefined, params: URLSearchParams) => {
  const utmAttrs = getUtmAttributes(params);
  if (user?.setUserAttributes) {
    user.setUserAttributes(utmAttrs);
  }
};

export const updateLocationPermissionStatus = async (user: IMParticleUser | undefined) => {
  if (!user) {
    return;
  }
  const status = await getNativeLocationPermissions();
  if (!status) {
    return;
  }
  if (isNativeIOS()) {
    user.setUserAttributes({
      'IOS Location Permissions': status,
    });
  } else {
    user.setUserAttributes({
      'Android Location Permissions': status,
    });
  }
};

export default setUserAttributes;
